// src/components/admin_panel/AdminLandingPagePerformance.js
import React, { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import AnalyzingIndicator from "./AnalyzingIndicator";

// Fixed hue order, reused consistently for the same metric everywhere on
// this page (chart lines, legend swatches) - never reassigned by filter/
// sort order. Chosen to echo colors already meaningful elsewhere on this
// page: indigo for views (matches the existing views bar), emerald for
// subscriptions (matches the existing "New Subscriptions" column), amber for
// accounts created.
const METRIC_COLORS = {
  views: "#667eea",
  accounts_created: "#f59e0b",
  subscriptions: "#10b981",
  cancellation_requests: "#ef4444",
};

// Status colors (not part of METRIC_COLORS' fixed categorical order above,
// since these aren't chart series - just headline tiles/table cells): red
// for "already cancelled" (critical), amber for "due to cancel" (warning,
// still active today) - reused from the app's existing error/warning
// palette rather than a new hue.
const STATUS_COLORS = {
  cancelled: "#ef4444",
  due_to_cancel: "#f59e0b",
};

// Same purple already used everywhere else on this page for a View →
// Subscription rate (the A/B table, the per-page funnel table) - reused
// here rather than picked fresh, so "purple = a conversion rate" stays a
// consistent visual rule across the whole tab.
const RATE_COLOR = "#8B5FBF";

// UK date order (DD/MM), not US (MM/DD) - dateStr is always "YYYY-MM-DD".
const formatShortDate = (dateStr) => {
  const [, month, day] = dateStr.split("-");
  return `${day}/${month}`;
};

// Custom tooltip: series identity is carried by the colored dot + text
// label together (never color alone), and every value is shown as plain
// text so it's readable without relying on color perception at all.
const TimeSeriesTooltip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload || payload.length === 0) return null;
  const format = valueFormatter || ((v) => v);
  return (
    <div style={{
      background: "white", border: "1px solid #e5e7eb", borderRadius: "8px",
      padding: "10px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", fontSize: "13px",
    }}>
      <div style={{ fontWeight: "600", marginBottom: "6px", color: "#111827" }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
          <span style={{ color: "#6b7280" }}>{entry.name}:</span>
          <span style={{ fontWeight: "600", color: "#111827" }}>{format(entry.value, entry.dataKey)}</span>
        </div>
      ))}
    </div>
  );
};

// Clickable legend - clicking a series toggles it on/off (see hiddenSeries
// in the main component). Identity/state is carried by both the swatch
// color AND the label text (strikethrough + dimmed when off), never color
// alone, so it stays legible for a colorblind reader.
const ToggleLegend = ({ payload, hiddenSeries, onToggle }) => (
  <div style={{ display: "flex", gap: "18px", flexWrap: "wrap", justifyContent: "center", marginTop: "6px" }}>
    {(payload || []).map((entry) => {
      const isHidden = hiddenSeries.has(entry.dataKey);
      return (
        <button
          key={entry.dataKey}
          type="button"
          onClick={() => onToggle(entry.dataKey)}
          style={{
            display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none",
            cursor: "pointer", padding: "2px 4px", fontSize: "13px",
            color: isHidden ? "#9ca3af" : "#374151",
            textDecoration: isHidden ? "line-through" : "none",
          }}
        >
          <span style={{
            width: "10px", height: "10px", borderRadius: "50%", flexShrink: 0,
            background: isHidden ? "#d1d5db" : entry.color,
          }} />
          {entry.value}
        </button>
      );
    })}
  </div>
);

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

// Matches mixpanel_service.py's CACHE_TTL_SECONDS - a request for a date
// range already fetched within this window is served straight from the
// server-side cache with no Mixpanel calls at all, so auto-fetching on
// every date-range change is cheap: it only actually queries Mixpanel when
// this range hasn't been seen recently. The Refresh button is disabled
// until this window has elapsed for the currently-shown range, since
// clicking it any sooner is guaranteed to return the exact same cached
// numbers - which used to look like the button (and the whole page) had
// silently stopped working, when really it just hadn't changed yet.
const SERVER_CACHE_TTL_SECONDS = 15 * 60;
const SERVER_CACHE_MINUTES = SERVER_CACHE_TTL_SECONDS / 60;
const DATE_CHANGE_DEBOUNCE_MS = 500;

// A/B rows list Advanced before Basic (matching the type shown in the Type
// column) rather than by variant letter, since the type is what visitors
// actually see and is what admins care about comparing. Falls back to
// variant-label order for ties (both same type, or type missing) so the
// row order stays stable rather than jumping around.
const LANDING_PAGE_TYPE_ORDER = { advanced: 0, basic: 1 };
const sortedVariantLabels = (variants) =>
  ['A', 'B']
    .filter((label) => variants[label])
    .sort((a, b) => {
      const orderA = LANDING_PAGE_TYPE_ORDER[variants[a].landing_page_type] ?? 2;
      const orderB = LANDING_PAGE_TYPE_ORDER[variants[b].landing_page_type] ?? 2;
      return orderA !== orderB ? orderA - orderB : a.localeCompare(b);
    });

const formatAge = (seconds) => {
  if (seconds == null) return null;
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
};

// Below this width the wide comparison tables (10 columns for the main
// funnel, 8 for A/B) force horizontal scrolling per row - annoying enough
// on mobile that each table switches to a stacked label/value card layout
// instead (see MobileStatCard below) rather than just shrinking font size.
const MOBILE_BREAKPOINT_PX = 700;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

// One label/value line inside a MobileStatCard - mirrors what a single
// table cell + its column header would have shown side by side.
const StatRow = ({ label, value, valueStyle }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: "13px", gap: "12px",
  }}>
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span style={{ fontWeight: "600", textAlign: "right", ...valueStyle }}>{value}</span>
  </div>
);

const MobileStatCard = ({ title, children }) => (
  <div style={{
    border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px 14px", marginBottom: "10px",
  }}>
    <div style={{ fontWeight: "600", marginBottom: "4px" }}>{title}</div>
    {children}
  </div>
);

// A single "headline number" tile - label above, value below - used for the
// Overall summary row. Color is optional and only ever paired with the
// label text right above it, never the sole way a value's meaning is conveyed.
const SummaryStatTile = ({ label, value, color }) => (
  <div style={{
    background: "white", borderRadius: "12px", padding: "16px 18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  }}>
    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>{label}</div>
    <div style={{ fontSize: "24px", fontWeight: "700", color: color || "#111827" }}>{value}</div>
  </div>
);

const formatRate = (rate) => (rate !== null && rate !== undefined ? `${rate}%` : '—');

const AdminLandingPagePerformance = ({ adminToken }) => {
  const isMobile = useIsMobile();
  const [fromDate, setFromDate] = useState(daysAgoStr(30));
  const [toDate, setToDate] = useState(todayStr());
  // '' means "All Sources" - not filtered. Populated from the API's
  // available_sources (derived from real data for the current date range),
  // so options always match what's actually filterable rather than a
  // hardcoded guess that might drift from what getTrafficSource() detects.
  const [sourceFilter, setSourceFilter] = useState("");
  const [availableSources, setAvailableSources] = useState([]);
  const [untracked, setUntracked] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [overall, setOverall] = useState(null);
  const [byType, setByType] = useState(null);
  const [daily, setDaily] = useState([]);
  // Which over-time chart series (by dataKey) are toggled off via the
  // legend - see ToggleLegend. A Set, not an array, so toggling is a cheap
  // membership check regardless of how many series exist.
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());
  const toggleSeries = useCallback((dataKey) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) next.delete(dataKey); else next.add(dataKey);
      return next;
    });
  }, []);
  const [variantComparisons, setVariantComparisons] = useState([]);
  const [variantError, setVariantError] = useState("");
  // Already part of the same landing-page-performance response as
  // funnel/overall/byType/variantComparisons above (see app.py's
  // get_landing_page_performance) - just not otherwise displayed on this
  // page (see the Meetup Group Finance tab for that), kept here purely to
  // feed the AI review below without a second fetch/Mixpanel call.
  const [meetupGroupFinance, setMeetupGroupFinance] = useState([]);
  // AI review of this tab's funnel/A-B data (see landing_page_performance_ai.py)
  // - same "run on demand, keep the latest, show/hide" pattern as
  // BlogAdmin.js's Content Coverage Review. Sends the funnel data this page
  // already has loaded (see handleRunPerformanceReview) rather than having
  // the backend re-query Mixpanel, since that has a tight rate-limited
  // query budget - only ever aggregate per-page/variant counts, never an
  // individual visitor record.
  const [performanceReview, setPerformanceReview] = useState(null);
  const [performanceReviewing, setPerformanceReviewing] = useState(false);
  const [performanceReviewError, setPerformanceReviewError] = useState("");
  const [showPerformanceReview, setShowPerformanceReview] = useState(false);
  const [cacheMeta, setCacheMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Client-side timestamp for when cacheMeta last arrived, so the "data as
  // of"/refresh-eligibility display can keep ticking live between fetches
  // instead of freezing at whatever age_seconds the server happened to
  // report at response time.
  const [cacheMetaReceivedAt, setCacheMetaReceivedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const fetchData = useCallback(async () => {
    if (!adminToken) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (sourceFilter) params.set('source', sourceFilter);
      const response = await fetch(`/api/admin/analytics/landing-page-performance?${params}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFunnel(data.funnel || []);
      setOverall(data.overall || null);
      setByType(data.by_type || null);
      setDaily(data.daily || []);
      setVariantComparisons(data.variant_comparisons || []);
      setVariantError(data.variant_comparisons_error || "");
      setMeetupGroupFinance(data.meetup_group_finance || []);
      setAvailableSources(data.available_sources || []);
      setUntracked(data.untracked || null);
      setCacheMeta(data.cache || null);
      setCacheMetaReceivedAt(Date.now());
    } catch (err) {
      console.error("Error fetching landing page performance:", err);
      setError(err.message || "Failed to load landing page performance");
    } finally {
      setLoading(false);
    }
  }, [adminToken, fromDate, toDate, sourceFilter]);

  useEffect(() => {
    if (!adminToken) return;
    fetch('/api/admin/analytics/landing-page-performance-review', { headers: { 'Authorization': `Bearer ${adminToken}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setPerformanceReview)
      .catch(() => setPerformanceReview(null));
  }, [adminToken]);

  const handleRunPerformanceReview = async () => {
    setPerformanceReviewing(true);
    setPerformanceReviewError("");
    try {
      const response = await fetch('/api/admin/analytics/landing-page-performance-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          funnel, overall, by_type: byType, variant_comparisons: variantComparisons,
          meetup_group_finance: meetupGroupFinance, from_date: fromDate, to_date: toDate,
        }),
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to run review');
      setPerformanceReview(await response.json());
      setShowPerformanceReview(true);
    } catch (err) {
      setPerformanceReviewError(err.message);
    } finally {
      setPerformanceReviewing(false);
    }
  };

  // Fetch whenever the admin token becomes available, the date range
  // changes, or the source filter changes (debounced slightly so picking a
  // date doesn't fire twice) - cheap thanks to the server-side cache above
  // (switching the source filter reuses the same cached per-source
  // breakdown server-side, with no extra Mixpanel calls), so there's no
  // need to make the admin remember to click Refresh just to see a new
  // range or source.
  useEffect(() => {
    if (!adminToken) return;
    const timer = setTimeout(() => { fetchData(); }, DATE_CHANGE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, fromDate, toDate, sourceFilter]);

  // Tick every second so the "data as of"/refresh-eligibility display updates live.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Live age of the currently-displayed data, factoring in both how old it
  // already was when the server reported it and how much client time has
  // passed since that response arrived.
  const liveAgeSeconds = (cacheMeta?.funnel && cacheMetaReceivedAt)
    ? cacheMeta.funnel.age_seconds + (nowTick - cacheMetaReceivedAt) / 1000
    : null;
  const isServingStaleFallback = !!(cacheMeta?.funnel?.stale || cacheMeta?.variant_comparisons?.stale);
  // Refreshing only makes sense once the server cache for this exact range
  // has actually expired (or it's already serving a stale fallback after a
  // prior failure) - otherwise a click is guaranteed to hand back the same
  // numbers, which is what used to look like a broken button.
  const canForceRefresh = isServingStaleFallback || liveAgeSeconds == null || liveAgeSeconds >= SERVER_CACHE_TTL_SECONDS;
  const refreshCooldownRemainingSeconds = liveAgeSeconds == null ? 0 : Math.max(SERVER_CACHE_TTL_SECONDS - liveAgeSeconds, 0);

  const maxViews = funnel && funnel.length > 0 ? Math.max(...funnel.map(row => row.views), 1) : 1;

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "10px", color: "#111827" }}>
        📈 Landing Page Performance
      </h2>
      <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
        Compares the homepage against each theme landing page, sourced live from Mixpanel, across
        the full funnel: page view &rarr; create-account page viewed &rarr; signup attempted &rarr;
        account created &rarr; checkout page viewed &rarr; checkout initiated &rarr; subscription
        started. Creating an account grants no access on its own — anyone without an active
        subscription is sent straight to checkout — so a started subscription is the conversion
        that actually matters, not the account. Use the Traffic Source filter below to see how a
        page performs for a specific source (Meetup, Eventbrite, Google, Meta, ...) instead of
        blended across all of them — a page can convert well for one source and poorly for another.
      </p>

      <p style={{
        color: "#6b7280",
        marginBottom: "20px",
        fontSize: "13px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "10px 14px",
      }}>
        ℹ️ Changing the date range fetches automatically. Mixpanel limits how many queries this
        page can make, so results for a given range are cached for up to {SERVER_CACHE_MINUTES}{" "}
        minutes — Refresh is only enabled once that window has passed, since clicking it sooner
        would just return the same numbers.
      </p>

      {!loading && !error && untracked && (untracked.accounts_created?.total > 0 || untracked.subscriptions?.total > 0) && (
        <div style={{
          background: "#fff7ed",
          border: "1px solid #fb923c",
          borderRadius: "8px",
          padding: "14px 16px",
          marginBottom: "20px",
        }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#7c2d12", marginBottom: "8px" }}>
            Attribution to a landing page (this date range)
          </div>
          <table style={{ borderCollapse: "collapse", fontSize: "13px", color: "#7c2d12" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", fontWeight: "600", padding: "2px 20px 2px 0" }}></th>
                <th style={{ textAlign: "right", fontWeight: "600", padding: "2px 20px" }}>Total</th>
                <th style={{ textAlign: "right", fontWeight: "600", padding: "2px 20px" }}>Attributed</th>
                <th style={{ textAlign: "right", fontWeight: "600", padding: "2px 0" }}>Not attributed</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Accounts created', untracked.accounts_created],
                ['New subscriptions', untracked.subscriptions],
              ].map(([label, row]) => (
                <tr key={label}>
                  <td style={{ padding: "2px 20px 2px 0" }}>{label}</td>
                  <td style={{ textAlign: "right", padding: "2px 20px" }}>{row.total}</td>
                  <td style={{ textAlign: "right", padding: "2px 20px" }}>{row.attributed}</td>
                  <td style={{ textAlign: "right", padding: "2px 0", fontWeight: "600" }}>
                    {row.unattributed}{row.total > 0 && ` (${Math.round((row.unattributed / row.total) * 100)}%)`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "8px", fontSize: "12px", color: "#9a3412" }}>
            "Not attributed" are real accounts/subscriptions already counted elsewhere — they're just missing from the
            table below since there's no landing page to attribute them to.
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", marginBottom: "12px", flexWrap: "wrap" }}>
        <div>
          <label style={{ display: "block", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "13px", color: "#374151", marginBottom: "4px" }}>Traffic Source</label>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", minWidth: "160px" }}
          >
            <option value="">All Sources</option>
            {availableSources.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={fetchData}
          disabled={loading || !canForceRefresh}
          title={!canForceRefresh
            ? `This data is still fresh (cached for up to ${SERVER_CACHE_MINUTES} minutes) - refreshing again won't return anything new for another ${Math.ceil(refreshCooldownRemainingSeconds / 60)}m`
            : undefined}
          style={{
            background: (loading || !canForceRefresh) ? "#a5b4fc" : "#667eea",
            color: "white",
            border: "none",
            padding: "9px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: (loading || !canForceRefresh) ? "not-allowed" : "pointer",
          }}
        >
          Refresh
        </button>
        {cacheMeta && cacheMeta.funnel && (
          <span style={{ fontSize: "13px", color: "#9ca3af" }}>
            Data as of {formatAge(liveAgeSeconds)}
            {!canForceRefresh && ` · fresh for another ${Math.ceil(refreshCooldownRemainingSeconds / 60)}m`}
          </span>
        )}
      </div>

      {(cacheMeta?.funnel?.stale || cacheMeta?.variant_comparisons?.stale) && (
        <div style={{
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: "8px",
          padding: "12px 16px",
          color: "#92400e",
          fontSize: "13px",
          marginBottom: "20px",
        }}>
          ⚠️ Mixpanel is rate-limiting requests right now, so this is showing the last successfully
          fetched data ({formatAge((cacheMeta.funnel?.stale ? cacheMeta.funnel : cacheMeta.variant_comparisons)?.age_seconds)}) instead of failing. It'll refresh automatically once Mixpanel allows it again.
        </div>
      )}

      {!loading && !error && overall && (
        <div style={{
          background: "white", borderRadius: "12px", padding: "20px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: "24px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <h3 style={{ margin: "0 0 4px 0", color: "#111827" }}>🤖 AI Review</h3>
              <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                {performanceReview
                  ? `Last reviewed: ${new Date(performanceReview.created_at).toLocaleString()}`
                  : "Not reviewed yet."}
              </p>
            </div>
            <button
              onClick={handleRunPerformanceReview}
              disabled={performanceReviewing}
              style={{
                background: performanceReviewing ? "#a5b4fc" : "#667eea", color: "white", border: "none",
                padding: "9px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: "600",
                cursor: performanceReviewing ? "not-allowed" : "pointer",
              }}
            >
              {performanceReviewing ? "Analyzing..." : "Analyze with Claude"}
            </button>
          </div>
          <p style={{ fontSize: "12px", color: "#9ca3af", margin: "8px 0 0 0" }}>
            Analyzes the funnel/A-B data already loaded on this page (current date range and source filter) - only
            aggregate per-page/variant counts, never an individual visitor record. Doesn't make an extra Mixpanel call.
          </p>

          {performanceReviewing && <AnalyzingIndicator color="#667eea" />}

          {performanceReviewError && (
            <div style={{
              marginTop: "15px", color: "#b91c1c", padding: "10px",
              border: "1px solid #ef4444", borderRadius: "6px", background: "#fee2e2", fontSize: "13px",
            }}>
              {performanceReviewError}
            </div>
          )}

          {performanceReview && (
            <div style={{ marginTop: "15px" }}>
              <button
                onClick={() => setShowPerformanceReview((v) => !v)}
                style={{
                  padding: "5px 15px", background: "#6c757d", color: "white",
                  border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px",
                }}
              >
                {showPerformanceReview ? "Hide review" : "Show review"}
              </button>

              {showPerformanceReview && (
                <div style={{ marginTop: "15px" }}>
                  <p style={{ fontSize: "14px", color: "#333" }}>{performanceReview.review_data.overall_summary}</p>

                  {performanceReview.review_data.findings?.length > 0 && (
                    <div style={{ marginBottom: "15px" }}>
                      <p style={{ fontSize: "13px", fontWeight: "bold", margin: "0 0 5px 0" }}>Findings</p>
                      {performanceReview.review_data.findings.map((f, i) => (
                        <div key={i} style={{ borderTop: "1px solid #eee", padding: "8px 0" }}>
                          <p style={{ margin: "0 0 3px 0", fontSize: "13px" }}><strong>[{f.severity}]</strong> {f.label}</p>
                          <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>{f.detail}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {performanceReview.review_data.recommendations?.length > 0 && (
                    <div>
                      <p style={{ fontSize: "13px", fontWeight: "bold", margin: "0 0 5px 0" }}>Recommendations</p>
                      {performanceReview.review_data.recommendations.map((r, i) => (
                        <div key={i} style={{ borderTop: "1px solid #eee", padding: "8px 0" }}>
                          <p style={{ margin: "0 0 3px 0", fontSize: "13px" }}><strong>[{r.priority}]</strong> {r.action}</p>
                          <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>{r.rationale}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!loading && !error && overall && (
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ marginBottom: "6px", color: "#111827" }}>Overall</h3>
          <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
            Totals across every landing page (homepage plus all theme landing pages) for this date
            range and traffic source filter. New Subscriptions counts every subscription <em>started</em> in
            this date range regardless of its status today; Cancelled/Due to Cancel then reflect the
            <em> current</em> Stripe status of that same group — not a count of cancellation events — so
            neither figure will match the Subscriptions tab's all-time totals (which cover every
            subscription ever, not just ones started in this date range).
          </p>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px",
            marginBottom: "24px",
          }}>
            <SummaryStatTile label="Views" value={overall.views} color={METRIC_COLORS.views} />
            <SummaryStatTile label="Accounts Created" value={overall.accounts_created} color={METRIC_COLORS.accounts_created} />
            <SummaryStatTile label="New Subscriptions" value={overall.subscriptions} color={METRIC_COLORS.subscriptions} />
            <SummaryStatTile label="Cancelled" value={overall.cancelled} color={STATUS_COLORS.cancelled} />
            <SummaryStatTile label="Due to Cancel" value={overall.due_to_cancel} color={STATUS_COLORS.due_to_cancel} />
            <SummaryStatTile label="View → Account" value={formatRate(overall.view_to_account_rate)} />
            <SummaryStatTile label="View → Subscription" value={formatRate(overall.view_to_subscription_rate)} />
          </div>

          <h3 style={{ marginBottom: "6px", color: "#111827" }}>Basic vs Advanced</h3>
          <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
            Same totals, split by each theme landing page's configured type (see Theme Landing
            Pages). The homepage isn't a theme landing page, so it's counted in Overall above but
            not here.
          </p>
          <div style={{
            background: "white", borderRadius: "12px", padding: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflowX: "auto", marginBottom: "24px",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", minWidth: "420px" }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>Metric</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Basic</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>Advanced</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Landing Pages', 'page_count', false],
                  ['Views', 'views', false],
                  ['Accounts Created', 'accounts_created', false],
                  ['New Subscriptions', 'subscriptions', false],
                  ['Cancelled', 'cancelled', false],
                  ['Due to Cancel', 'due_to_cancel', false],
                  ['View → Account', 'view_to_account_rate', true],
                  ['View → Subscription', 'view_to_subscription_rate', true],
                ].map(([label, key, isRate]) => (
                  <tr key={key} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "10px 12px", fontWeight: "500" }}>{label}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      {isRate ? formatRate(byType?.basic?.[key]) : (byType?.basic?.[key] ?? 0)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      {isRate ? formatRate(byType?.advanced?.[key]) : (byType?.advanced?.[key] ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {daily && daily.length > 0 && (
            <>
              <h3 style={{ marginBottom: "6px", color: "#111827" }}>Over Time</h3>
              <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
                Daily totals across every landing page (same "attributed" figures as Overall above)
                for this date range and traffic source filter. Views run at a much larger scale than
                accounts created, new subscriptions, and cancellation requests, so Views uses its own
                axis on the left (indigo ticks); Accounts Created, New Subscriptions, and Cancellation
                Requests share a right-hand axis; the View → New Subscription conversion rate uses its
                own percentage axis further right (purple ticks), since a percentage isn't comparable
                to a raw count on either other axis — click a line's name below the chart to hide/show
                it, which also helps compare series that share an axis. Cancellation Requests is the
                day a subscriber <em>asked</em> to cancel (Stripe's canceled_at), not the day their
                subscription actually ends — that's a different, later date, and isn't plotted here.
                Cancelled/Due to Cancel in the totals above are a snapshot of current status rather
                than a dated event, so they aren't shown on this chart either. A day with zero views
                has no rate to plot (a gap, not a drop to 0%).
              </p>
              <p style={{
                color: "#92400e",
                marginBottom: "12px",
                fontSize: "13px",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "8px",
                padding: "10px 14px",
              }}>
                ⚠️ <strong>Cancellation Requests only covers subscriptions that were themselves
                created within the selected date range</strong> (the same population as New
                Subscriptions above) — the same as Cancelled/Due to Cancel in the totals above. A
                subscription that started <em>before</em> this range and requests cancellation
                during it is not counted here, so this understates total cancellation-request
                activity for any range that doesn't start from this landing page's earliest signups.
              </p>
              <div style={{
                background: "white", borderRadius: "12px", padding: "20px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={daily} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 12, fill: "#6b7280" }} />
                    <YAxis
                      yAxisId="views" allowDecimals={false} width={44}
                      tick={{ fontSize: 12, fill: METRIC_COLORS.views }}
                    />
                    <YAxis
                      yAxisId="funnel" orientation="right" allowDecimals={false} width={44}
                      tick={{ fontSize: 12, fill: "#6b7280" }}
                    />
                    <YAxis
                      yAxisId="rate" orientation="right" width={48}
                      tick={{ fontSize: 12, fill: RATE_COLOR }}
                      tickFormatter={(v) => `${v}%`} domain={[0, (dataMax) => Math.max(dataMax, 1)]}
                    />
                    <Tooltip content={<TimeSeriesTooltip valueFormatter={(v, dataKey) => (dataKey === 'view_to_subscription_rate' ? `${v}%` : v)} />} />
                    <Legend content={<ToggleLegend hiddenSeries={hiddenSeries} onToggle={toggleSeries} />} />
                    <Line
                      yAxisId="views" type="monotone" dataKey="views" name="Views" hide={hiddenSeries.has('views')}
                      stroke={METRIC_COLORS.views} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="funnel" type="monotone" dataKey="accounts_created" name="Accounts Created"
                      hide={hiddenSeries.has('accounts_created')}
                      stroke={METRIC_COLORS.accounts_created} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="funnel" type="monotone" dataKey="subscriptions" name="New Subscriptions"
                      hide={hiddenSeries.has('subscriptions')}
                      stroke={METRIC_COLORS.subscriptions} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="funnel" type="monotone" dataKey="cancellation_requests" name="Cancellation Requests"
                      hide={hiddenSeries.has('cancellation_requests')}
                      stroke={METRIC_COLORS.cancellation_requests} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="rate" type="monotone" dataKey="view_to_subscription_rate" name="View → New Subscription Rate"
                      hide={hiddenSeries.has('view_to_subscription_rate')} connectNulls
                      stroke={RATE_COLOR} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

        </div>
      )}

      {loading && (
        <div style={{ padding: "40px", textAlign: "center", fontSize: "18px", color: "#666" }}>
          Loading...
        </div>
      )}

      {!loading && error && (
        <div style={{
          background: "#fee2e2",
          border: "1px solid #ef4444",
          borderRadius: "8px",
          padding: "16px",
          color: "#991b1b"
        }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <div>
          <h3 style={{ marginBottom: "6px", color: "#111827" }}>🧪 A/B Test Results</h3>
          <p style={{ color: "#666", marginBottom: "12px", fontSize: "14px" }}>
            For landing pages with an A and a B variant configured (see Theme Landing Pages),
            compares views, accounts created, and new subscriptions between variants, based on
            <strong> subscriptions</strong>, not accounts — an account alone grants no access, so
            subscribing is the conversion that actually matters. New Subscriptions counts every
            subscription started in this date range regardless of its status today; Active (NOT
            due to cancel) narrows that to ones still active with no cancellation scheduled — the
            same "Active (NOT due to cancel)" bucket shown on the Subscriptions tab.
          </p>
          <div style={{
            background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: "8px",
            padding: "12px 16px", marginBottom: "16px", fontSize: "13px", color: "#3730a3",
          }}>
            <strong>How to read "statistically significant":</strong> the <em>p-value</em> is
            the chance we'd see a gap this big between two variants even if they were truly no
            different from each other — the smaller it is, the more likely the gap is real
            rather than a fluke. We call a result "significant" once that chance drops below 5%
            (p &lt; 0.05, shown here as 95%+ confidence). Above that threshold, treat the
            leading variant as "ahead so far" rather than a proven winner — with small amounts
            of traffic, an early lead can easily be noise that evens out later.
          </div>

          {variantError && (
            <div style={{
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "8px",
              padding: "12px 16px",
              color: "#991b1b",
              fontSize: "13px",
              marginBottom: "16px",
            }}>
              ⚠️ {variantError}
            </div>
          )}

          {!variantError && variantComparisons.length === 0 && (
            <div style={{ padding: "24px", textAlign: "center", color: "#9ca3af", background: "white", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              No landing pages have an A and a B variant configured yet (see Theme Landing Pages).
            </div>
          )}

          {variantComparisons.map((row) => {
            const sig = row.significance;
            let banner = {
              bg: "#f3f4f6", border: "#d1d5db", color: "#374151",
              text: "⏳ Not enough data yet — at least one variant has no views so far, so there's nothing to compare yet.",
            };
            if (sig) {
              const chancePct = Math.round(sig.p_value * 1000) / 10; // p_value as a % chance the gap is just noise
              if (sig.significant) {
                banner = {
                  bg: "#d1fae5", border: "#10b981", color: "#065f46",
                  text: `✅ Variant ${sig.winner} is the likely winner — only about a ${chancePct}% chance this gap is just random noise `
                    + `(p=${sig.p_value}, ${sig.confidence_pct}% confidence).`,
                };
              } else {
                let moreDataText;
                if (sig.practically_unreachable) {
                  moreDataText = "The gap between variants so far is too small (or there may be no real difference) to "
                    + "resolve with the amount of traffic this test is likely to get.";
                } else if (sig.more_views_per_variant) {
                  moreDataText = `At the rates seen so far, each variant would need roughly `
                    + `${sig.more_views_per_variant.toLocaleString()} more views before we could confidently call a winner.`;
                } else {
                  moreDataText = "Keep collecting data.";
                }
                banner = {
                  bg: "#fef3c7", border: "#f59e0b", color: "#92400e",
                  text: `⏳ Not yet a confident result — there's still about a ${chancePct}% chance this gap is just random noise `
                    + `(p=${sig.p_value}, ${sig.confidence_pct}% confidence). ${moreDataText}`,
                };
              }
            }

            return (
              <div key={row.url_slug} style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "8px" }}>
                  <h4 style={{ margin: 0 }}>/theme/{row.url_slug}{row.theme_name && <span style={{ color: "#9ca3af", fontWeight: "400", fontSize: "13px" }}> {row.theme_name}</span>}</h4>
                </div>

                {isMobile ? (
                  <div style={{ marginTop: "12px" }}>
                    {sortedVariantLabels(row.variants).map((label) => {
                      const v = row.variants[label];
                      const isWinner = sig && sig.significant && sig.winner === label;
                      return (
                        <MobileStatCard
                          key={label}
                          title={`Variant ${label}${isWinner ? ' 🏆' : ''}`}
                        >
                          <StatRow label="Type" value={v.landing_page_type || '—'} valueStyle={{ textTransform: "capitalize" }} />
                          <StatRow label="Traffic Weight" value={v.traffic_weight !== null && v.traffic_weight !== undefined ? v.traffic_weight : '—'} />
                          <StatRow label="Views" value={v.views} />
                          <StatRow label="Accounts Created" value={v.accounts_created} />
                          <StatRow label="New Subscriptions" value={v.subscriptions} valueStyle={{ color: "#10b981" }} />
                          <StatRow label="Active (NOT due to cancel)" value={v.active_subscriptions} valueStyle={{ color: "#059669" }} />
                          <StatRow
                            label="View → Subscription"
                            value={v.view_to_subscription_rate !== null ? `${v.view_to_subscription_rate}%` : '—'}
                            valueStyle={{ color: "#8B5FBF" }}
                          />
                        </MobileStatCard>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", marginTop: "12px", minWidth: "820px" }}>
                    <thead>
                      <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                        <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>Variant</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>Type</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>Traffic Weight</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>Views</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>Accounts Created</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>New Subscriptions</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>Active (NOT due to cancel)</th>
                        <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>View &rarr; Subscription</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedVariantLabels(row.variants).map((label) => {
                        const v = row.variants[label];
                        const isWinner = sig && sig.significant && sig.winner === label;
                        return (
                          <tr key={label} style={{ borderBottom: "1px solid #e5e7eb", background: isWinner ? "#ecfdf5" : "transparent" }}>
                            <td style={{ padding: "8px", fontWeight: "600" }}>{label}{isWinner ? ' 🏆' : ''}</td>
                            <td style={{ padding: "8px", textAlign: "center", textTransform: "capitalize" }}>{v.landing_page_type || '—'}</td>
                            <td style={{ padding: "8px", textAlign: "center" }}>{v.traffic_weight !== null && v.traffic_weight !== undefined ? `${v.traffic_weight}` : '—'}</td>
                            <td style={{ padding: "8px", textAlign: "center" }}>{v.views}</td>
                            <td style={{ padding: "8px", textAlign: "center" }}>{v.accounts_created}</td>
                            <td style={{ padding: "8px", textAlign: "center", color: "#10b981", fontWeight: "600" }}>{v.subscriptions}</td>
                            <td style={{ padding: "8px", textAlign: "center", color: "#059669", fontWeight: "600" }}>{v.active_subscriptions}</td>
                            <td style={{ padding: "8px", textAlign: "center", fontWeight: "600", color: "#8B5FBF" }}>
                              {v.view_to_subscription_rate !== null ? `${v.view_to_subscription_rate}%` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}

                <div style={{
                  marginTop: "12px",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: banner.bg,
                  border: `1px solid ${banner.border}`,
                  color: banner.color,
                  fontSize: "13px",
                  fontWeight: "500",
                }}>
                  {banner.text}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && (!funnel || funnel.length === 0) && (
        <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
          No data for this date range
          {sourceFilter && ` and source (${availableSources.find(s => s.value === sourceFilter)?.label || sourceFilter})`}
        </div>
      )}

      {!loading && !error && funnel && funnel.length > 0 && isMobile && (
        <div style={{ marginTop: "32px" }}>
          <h3 style={{ marginBottom: "10px", color: "#111827" }}>Per-page funnel</h3>
          {funnel.map((row) => (
            <MobileStatCard
              key={row.landing_page}
              title={row.landing_page === 'homepage' ? 'Homepage' : row.landing_page.replace(/^theme:/, '')}
            >
              <StatRow label="Views" value={row.views} />
              <StatRow label="Account Page Viewed" value={row.account_page_views} />
              <StatRow label="Signup Attempted" value={row.signup_attempts} />
              <StatRow label="Accounts Created" value={row.accounts_created} />
              <StatRow label="Checkout Viewed" value={row.checkout_views} />
              <StatRow label="Checkout Initiated" value={row.checkout_initiated} />
              <StatRow label="New Subscriptions" value={row.subscriptions} valueStyle={{ color: "#10b981" }} />
              <StatRow label="View → Account" value={row.view_to_account_rate !== null ? `${row.view_to_account_rate}%` : '—'} />
              <StatRow
                label="View → Subscription"
                value={row.view_to_subscription_rate !== null ? `${row.view_to_subscription_rate}%` : '—'}
                valueStyle={{ color: "#8B5FBF" }}
              />
            </MobileStatCard>
          ))}
        </div>
      )}

      {!loading && !error && funnel && funnel.length > 0 && !isMobile && (
        <div style={{ background: "white", borderRadius: "12px", padding: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflowX: "auto", marginTop: "32px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", minWidth: "980px" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Landing Page</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Views</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Account Page Viewed</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Signup Attempted</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Accounts Created</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Checkout Viewed</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Checkout Initiated</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>New Subscriptions</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>View &rarr; Account</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>View &rarr; Subscription</th>
              </tr>
            </thead>
            <tbody>
              {funnel.map((row) => (
                <tr key={row.landing_page} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "12px", fontWeight: "500", whiteSpace: "nowrap" }}>
                    {row.landing_page === 'homepage' ? 'Homepage' : row.landing_page.replace(/^theme:/, '')}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                      <div style={{ width: "60px", height: "8px", background: "#e5e7eb", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{
                          width: `${(row.views / maxViews) * 100}%`,
                          height: "100%",
                          background: "#667eea",
                        }} />
                      </div>
                      <span>{row.views}</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>{row.account_page_views}</td>
                  <td style={{ padding: "12px", textAlign: "center" }}>{row.signup_attempts}</td>
                  <td style={{ padding: "12px", textAlign: "center" }}>{row.accounts_created}</td>
                  <td style={{ padding: "12px", textAlign: "center" }}>{row.checkout_views}</td>
                  <td style={{ padding: "12px", textAlign: "center" }}>{row.checkout_initiated}</td>
                  <td style={{ padding: "12px", textAlign: "center", color: "#10b981", fontWeight: "600" }}>
                    {row.subscriptions}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    {row.view_to_account_rate !== null ? `${row.view_to_account_rate}%` : '—'}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: "#8B5FBF" }}>
                    {row.view_to_subscription_rate !== null ? `${row.view_to_subscription_rate}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminLandingPagePerformance;
